import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  LocalOffer as PlanIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

interface DashboardStats {
  totalDoctors: number;
  activePlans: number;
  totalTranscriptions: number;
}

const OwnerDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalDoctors: 0,
    activePlans: 0,
    totalTranscriptions: 0
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [doctorsResponse, plansResponse, transcriptionsResponse] = await Promise.all([
        axios.get('/api/owner/doctors'),
        axios.get('/api/owner/plans'),
        axios.get('/api/owner/transcriptions')
      ]);

      setStats({
        totalDoctors: doctorsResponse.data.length,
        activePlans: plansResponse.data.length,
        totalTranscriptions: transcriptionsResponse.data.length
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'medium' }}>
          Bem-vindo(a), {user?.name}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Aqui está um resumo do seu sistema
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/owner/plans')}
            >
              Novo Plano
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<ListIcon />}
              onClick={() => navigate('/owner/doctors')}
            >
              Gerenciar Médicos
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              height: 140,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon sx={{ mr: 1 }} />
              <Typography component="h2" variant="h6">
                Total de Médicos
              </Typography>
            </Box>
            <Typography component="p" variant="h3">
              {stats.totalDoctors}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              height: 140,
              bgcolor: 'success.light',
              color: 'success.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PlanIcon sx={{ mr: 1 }} />
              <Typography component="h2" variant="h6">
                Planos Ativos
              </Typography>
            </Box>
            <Typography component="p" variant="h3">
              {stats.activePlans}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              height: 140,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssessmentIcon sx={{ mr: 1 }} />
              <Typography component="h2" variant="h6">
                Total de Transcrições
              </Typography>
            </Box>
            <Typography component="p" variant="h3">
              {stats.totalTranscriptions}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="h2">
              Atividade Recente
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography color="text.secondary" gutterBottom>
                Últimos Médicos Cadastrados
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Nenhum médico cadastrado recentemente
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography color="text.secondary" gutterBottom>
                Últimas Transcrições
              </Typography>
              <Typography variant="body1">
                Nenhuma transcrição realizada recentemente
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <Button size="small" color="primary" onClick={() => navigate('/owner/doctors')}>
            Ver Todos os Médicos
          </Button>
          <Button size="small" color="primary">
            Ver Todas as Transcrições
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default OwnerDashboard;
